import type { FC } from 'react'
import { useState } from 'react'
import {
  CwsP,
  CwsH4,
  CwsButton,
  CwsModal,
  CwsModalBody,
  CwsModalFooter,
  CwsGrid,
  CwsGridCol,
  CwsGridRow,
} from '@components/cws'
import { useTranslate } from '@hooks/useTranslation'
import type { FeedbackMessage, MessageKey } from '@components/support/ContactUs/Feedback'
import { Feedback } from '@components/support/ContactUs/Feedback'
import { useRouter } from 'next/router'
import axios from 'axios'
// @ts-ignore
import { modal } from 'cms-web-styleguide/dist/utils/utils'
import type { Locale } from '@model/locales'
import { KK_LANG_IDS } from '@model/locales'
import type { FeedbackResult } from '@api/feedback'
import { contactFormValidator, EMAIL, MESSAGE, NAME, PHONE } from '@utils/contactFormValidator'

const ContactUs: FC = () => {
  const { translate } = useTranslate()
  const [visible, setVisible] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const [message, setMessage] = useState<FeedbackMessage>({
    [NAME]: '',
    [EMAIL]: '',
    [PHONE]: '',
    [MESSAGE]: '',
  })

  const allFiled = !(Object.keys(message) as MessageKey[])
    .map((key) => contactFormValidator(key, message[key]))
    .includes(false)

  const { locale } = useRouter()
  const languageId = KK_LANG_IDS[locale as Locale]

  const copyright = translate('w2.copyright.message', {
    privacyPolicy: `<a class="cws-a cws-a--theme-white cws-a--size-tiny cws-a--inline cws-a--color-dark-grey" target="_blank" href="https://policies.google.com/privacy?hl=${locale}">${translate(
      'footer.privacy.policy'
    )}</a>`,
    termsOfService: `<a class="cws-a cws-a--theme-white cws-a--size-tiny cws-a--inline cws-a--color-dark-grey" target="_blank" href="https://policies.google.com/terms?hl=${locale}">${translate(
      'w2.terms.of.service'
    )}</a>`,
  })

  const sendFeedback = async () => {
    const contactUsMessage = (Object.keys(message) as MessageKey[]).reduce((acc, cur) => {
      return { ...acc, [translate(cur)]: message[cur] }
    }, {})
    const { data } = await axios.post<FeedbackResult>('/api/feedback/', {
      message: JSON.stringify(contactUsMessage),
      languageId,
      title: translate('w2.feedback.title.email'),
    })
    if (data.status === 'success') {
      modal.addEventModalShow('feedback-success-modal')
      setVisible(false)
      setIsSuccess(true)
    }
  }

  return (
    <>
      {!isSuccess && (
        <CwsModal id="feedback-modal" hideCloseButton visible={visible} onClose={() => setVisible(false)}>
          <CwsModalBody>
            <Feedback setMessage={setMessage} message={message} />
          </CwsModalBody>
          <CwsModalFooter>
            <div className="w-full flex flex-col items-center gap-3.5">
              <CwsButton disabled={!allFiled} onClick={sendFeedback}>
                {translate('w2.feedback.send')}
              </CwsButton>
              <CwsP color="dark-grey" dangerouslySetInnerHTML={{ __html: copyright }} size="tiny" />
            </div>
          </CwsModalFooter>
        </CwsModal>
      )}

      <CwsModal id="feedback-success-modal" visible={isSuccess} onClose={() => setIsSuccess(false)}>
        <CwsModalBody>
          <CwsGrid>
            <CwsGridRow>
              <CwsGridCol>
                <CwsH4>{translate('contact.feedback.event.sent')}</CwsH4>
              </CwsGridCol>
            </CwsGridRow>
          </CwsGrid>
        </CwsModalBody>
      </CwsModal>
    </>
  )
}

export default ContactUs
